// Live dictation over Amazon Transcribe streaming.
//
// The browser opens a presigned wss:// URL (minted by the API via the
// createTranscribePresigner mutation) and exchanges AWS event-stream frames:
// binary messages with a CRC-checked prelude, string headers, and a payload.
// Microphone audio goes up as AudioEvent frames of 16-bit little-endian PCM
// (produced by /audio-worklets/pcm-processor.js); punctuated transcripts come
// back as TranscriptEvent frames of JSON.
//
// Unlike the Web Speech API (lib/speech.js), transcripts arrive with
// punctuation and capitalization, in any of the languages the API allows.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)

  for (let n = 0; n < 256; n++) {
    let crc = n

    for (let k = 0; k < 8; k++)
      crc = crc & 1 ? 0xEDB88320 ^ (crc >>> 1) : crc >>> 1

    table[n] = crc >>> 0
  }

  return table
})()

export const crc32 = bytes => {
  let crc = 0xFFFFFFFF

  for (let i = 0; i < bytes.length; i++)
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8)

  return (crc ^ 0xFFFFFFFF) >>> 0
}

const AUDIO_EVENT_HEADERS = {
  ':content-type': 'application/octet-stream',
  ':event-type': 'AudioEvent',
  ':message-type': 'event'
}

const STRING_HEADER_TYPE = 7

const encodeHeaders = headers => {
  const encoder = new TextEncoder()
  const parts = Object.entries(headers).map(([name, value]) => {
    const nameBytes = encoder.encode(name)
    const valueBytes = encoder.encode(value)
    const part = new Uint8Array(1 + nameBytes.length + 1 + 2 + valueBytes.length)
    const view = new DataView(part.buffer)

    part[0] = nameBytes.length
    part.set(nameBytes, 1)
    part[1 + nameBytes.length] = STRING_HEADER_TYPE
    view.setUint16(2 + nameBytes.length, valueBytes.length)
    part.set(valueBytes, 4 + nameBytes.length)

    return part
  })

  const merged = new Uint8Array(parts.reduce((total, part) => total + part.length, 0))

  parts.reduce((offset, part) => {
    merged.set(part, offset)

    return offset + part.length
  }, 0)

  return merged
}

/** Frames one event-stream message: prelude + CRC, headers, payload, message CRC. */
export const encodeEvent = (headers, payload) => {
  const headerBytes = encodeHeaders(headers)
  const totalLength = 16 + headerBytes.length + payload.length
  const bytes = new Uint8Array(totalLength)
  const view = new DataView(bytes.buffer)

  view.setUint32(0, totalLength)
  view.setUint32(4, headerBytes.length)
  view.setUint32(8, crc32(bytes.subarray(0, 8)))
  bytes.set(headerBytes, 12)
  bytes.set(payload, 12 + headerBytes.length)
  view.setUint32(totalLength - 4, crc32(bytes.subarray(0, totalLength - 4)))

  return bytes
}

/**
 * Parses an event-stream message into {headers, payload}. Throws on frames
 * whose declared length or CRCs don't match, so corrupted or truncated
 * messages are rejected deterministically instead of being misparsed.
 */
export const decodeEvent = buffer => {
  const bytes = new Uint8Array(buffer)
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

  if (bytes.byteLength < 16 || view.getUint32(0) !== bytes.byteLength)
    throw new Error('Event stream frame length mismatch')

  if (view.getUint32(8) !== crc32(bytes.subarray(0, 8)))
    throw new Error('Event stream prelude CRC mismatch')

  if (view.getUint32(bytes.byteLength - 4) !== crc32(bytes.subarray(0, bytes.byteLength - 4)))
    throw new Error('Event stream message CRC mismatch')

  const headersEnd = 12 + view.getUint32(4)

  if (headersEnd > bytes.byteLength - 4)
    throw new Error('Event stream header section out of bounds')

  const decoder = new TextDecoder()
  const headers = {}
  let offset = 12

  while (offset < headersEnd) {
    const nameLength = bytes[offset]

    offset += 1
    const name = decoder.decode(bytes.subarray(offset, offset + nameLength))

    offset += nameLength + 1 // +1 skips the header value type (always string here)

    if (offset + 2 > headersEnd)
      throw new Error('Event stream header entry out of bounds')

    const valueLength = view.getUint16(offset)

    offset += 2
    headers[name] = decoder.decode(bytes.subarray(offset, offset + valueLength))
    offset += valueLength
  }

  // Entries must consume the header section exactly; a leftover or overrun
  // means the lengths were inconsistent and the parsed headers are garbage.
  if (offset !== headersEnd)
    throw new Error('Event stream header section malformed')

  return {headers, payload: bytes.subarray(headersEnd, bytes.byteLength - 4)}
}

/**
 * Maps a TranscriptEvent to the (finalText, interimText) contract shared with
 * lib/speech.js: finalText is newly finalized speech (Transcribe never
 * re-sends a finalized segment), interimText the live in-progress guess.
 * Finalized segments get a trailing space so consecutive segments read as
 * separate sentences when concatenated; interim is the latest partial only,
 * since callers display it as the single current in-progress phrase.
 */
export const transcriptTexts = transcriptEvent => {
  const results = (transcriptEvent && transcriptEvent.Transcript && transcriptEvent.Transcript.Results) || []
  let finalText = ''
  let interimText = ''

  results.forEach(result => {
    const transcript = (result.Alternatives && result.Alternatives[0] && result.Alternatives[0].Transcript) || ''

    if (!transcript) return

    if (result.IsPartial) interimText = transcript
    else finalText += `${transcript} `
  })

  return {finalText, interimText}
}

export const transcribeStreamingSupported = () =>
  typeof window !== 'undefined' &&
  typeof WebSocket !== 'undefined' &&
  typeof AudioWorkletNode !== 'undefined' &&
  typeof (window.AudioContext || window.webkitAudioContext) !== 'undefined'

/**
 * Starts a dictation session for a live microphone stream. Resolves with
 * {stop} once audio is flowing; rejects if the socket or audio pipeline can't
 * be established (callers fall back to the Web Speech API).
 *
 * Callbacks match lib/speech.js: onResult(finalText, interimText) as
 * transcripts arrive, onEnd() exactly once when the session is over —
 * including after stop(), so callers must ignore ends they initiated.
 * pause()/resume() hold the transcription: while paused the socket gets
 * silence of the same shape, so nothing said lands and Transcribe does not
 * close the stream for lack of audio (it would after ~15s). The boundary is
 * drawn in the worklet, which holds the partial chunk: pause() asks it to
 * send the words said so far and go quiet, and frames stay live until it
 * answers; resume() has it drop whatever it captured while paused, and
 * frames stay silent until it answers. Requests are numbered and their
 * answers applied in the order the worklet gives them, which is the order
 * of the frames, so the stream always mirrors the worklet's phase.
 * `isPaused` lets the caller's own paused state apply from the very first
 * frame, before the promise has resolved and pause() could be called.
 */
export const createTranscribeDictation = ({stream, presigner, onResult, onEnd, isPaused = () => false}) => new Promise((resolve, reject) => {
  const socket = new WebSocket(presigner.presignedUrl)

  socket.binaryType = 'arraybuffer'

  let audioContext = null
  let workletNode = null
  let sourceNode = null
  let settled = false
  let ended = false
  // Follows the worklet, not the caller: pause() and resume() each ask the
  // worklet to change state and `paused` flips only on its reply, so the
  // frames forwarded as audio are exactly the ones the worklet produced
  // live (the pre-pause tail included, nothing captured while paused).
  let paused = false
  // Each request is numbered and the worklet echoes the number back. The
  // worklet handles requests in order and the port delivers its replies in
  // order with the frames, so applying every reply as it arrives keeps
  // `paused` equal to the worklet's phase at the moment each frame was
  // produced — a quick Resume → Pause still forwards the words spoken
  // between the two as audio. Replies to unknown ids are ignored.
  let transitionId = 0
  const pendingTransitions = new Map()

  const endOnce = () => {
    if (ended) return

    ended = true
    onEnd()
  }

  // Failures before the session is established surface only through the
  // promise rejection. Marking the session ended keeps later socket events
  // (error, close) from also firing onEnd — callers fall back once, not twice.
  const rejectOnce = error => {
    settled = true
    ended = true
    reject(error)
  }

  const teardownAudio = () => {
    if (sourceNode) sourceNode.disconnect()

    if (workletNode) {
      workletNode.port.onmessage = null
      workletNode.disconnect()
    }

    if (audioContext && audioContext.state !== 'closed')
      audioContext.close()

    audioContext = workletNode = sourceNode = null
  }

  const endStream = () => {
    teardownAudio()

    if (socket.readyState === WebSocket.OPEN) {
      // An empty AudioEvent tells Transcribe the stream is done; it finalizes
      // any partial results (which still arrive via onResult) and closes the
      // socket from its side. The timeout covers a server that never does.
      socket.send(encodeEvent(AUDIO_EVENT_HEADERS, new Uint8Array(0)))
      setTimeout(() => socket.close(), 3000)
    } else {
      socket.close()
    }
  }

  const stop = () => {
    if (socket.readyState !== WebSocket.OPEN || !workletNode) {
      endStream()

      return
    }

    // The worklet buffers up to a chunk of audio (~100ms) — the user's last
    // words. Ask it to flush before the end-of-stream frame; the timeout
    // keeps stop() from hanging if the worklet never answers.
    let finished = false

    const finish = () => {
      if (finished) return

      finished = true
      endStream()
    }

    const forwardAudio = workletNode.port.onmessage

    workletNode.port.onmessage = event => {
      if (event.data === 'flushed') finish()
      else forwardAudio(event)
    }

    workletNode.port.postMessage('flush')
    setTimeout(finish, 250)
  }

  const setupAudio = () => {
    const Context = window.AudioContext || window.webkitAudioContext

    audioContext = new Context({sampleRate: presigner.sampleRate})

    return audioContext.audioWorklet.addModule('/audio-worklets/pcm-processor.js').then(() => {
      // The session may have failed (and torn down the audio context) while
      // the worklet module was still loading.
      if (ended) return

      paused = Boolean(isPaused())
      sourceNode = audioContext.createMediaStreamSource(stream)
      workletNode = new AudioWorkletNode(audioContext, 'pcm-processor', {
        processorOptions: {targetSampleRate: presigner.sampleRate, paused}
      })

      workletNode.port.onmessage = event => {
        if (event.data && (event.data.type === 'paused' || event.data.type === 'resumed')) {
          if (pendingTransitions.has(event.data.id)) {
            paused = pendingTransitions.get(event.data.id)
            pendingTransitions.delete(event.data.id)
          }

          return
        }

        if (!(event.data instanceof ArrayBuffer) || socket.readyState !== WebSocket.OPEN) return

        // A zero-filled frame is silence in signed 16-bit PCM. The worklet
        // already sends silence while paused; this keeps it so for any frame
        // it produced before its reply, which the port delivers first.
        const samples = paused ? new Uint8Array(event.data.byteLength) : new Uint8Array(event.data)

        socket.send(encodeEvent(AUDIO_EVENT_HEADERS, samples))
      }

      // Not connected to the destination on purpose: transcription only, no
      // microphone monitoring through the speakers.
      sourceNode.connect(workletNode)
    })
  }

  socket.onopen = () => {
    setupAudio()
      .then(() => {
        // The session may have already failed (e.g. an in-socket exception
        // arrived while the worklet was still loading); don't resurrect it.
        if (ended) return

        settled = true

        const transition = (type, nextPaused) => {
          const id = ++transitionId

          pendingTransitions.set(id, nextPaused)
          if (workletNode) workletNode.port.postMessage({type, id})
        }
        const pause = () => transition('pause', true)
        const resume = () => transition('resume', false)

        // The caller's paused state may have changed while the worklet was
        // loading; the worklet started from the state at creation.
        if (Boolean(isPaused()) !== paused) (isPaused() ? pause : resume)()

        resolve({stop, pause, resume})
      })
      .catch(error => {
        stop()
        rejectOnce(error)
      })
  }

  socket.onmessage = event => {
    let decoded

    try {
      decoded = decodeEvent(event.data)
    } catch (error) {
      // A corrupt frame can't be resynchronized reliably; skip it. TLS makes
      // this effectively unreachable, so losing one event beats ending the
      // whole dictation session.
      // eslint-disable-next-line no-console
      console.warn('Discarding malformed Transcribe frame:', error)

      return
    }

    const {headers, payload} = decoded

    if (headers[':message-type'] === 'event' && headers[':event-type'] === 'TranscriptEvent') {
      let transcriptEvent

      try {
        transcriptEvent = JSON.parse(new TextDecoder().decode(payload))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Discarding unparseable TranscriptEvent payload:', error)

        return
      }

      const {finalText, interimText} = transcriptTexts(transcriptEvent)

      if (finalText || interimText) onResult(finalText, interimText)
    } else if (headers[':message-type'] === 'exception') {
      const message = `Transcribe stream exception (${headers[':exception-type']}): ${new TextDecoder().decode(payload)}`

      // eslint-disable-next-line no-console
      console.warn(message)
      stop()

      // Before the session is established the exception must reject the open
      // promise instead of firing onEnd, or the caller would fall back twice.
      if (settled) endOnce()
      else rejectOnce(new Error(message))
    }
  }

  socket.onerror = () => {
    teardownAudio()

    if (settled) endOnce()
    else rejectOnce(new Error('Transcribe socket failed'))
  }

  socket.onclose = () => {
    teardownAudio()

    if (settled) endOnce()
    else rejectOnce(new Error('Transcribe socket closed before opening'))
  }
})
